import { ApiError } from "./errors";

interface ValidationItem {
  loc?: unknown;
  msg?: unknown;
}

/** ``[{"loc": ["body", "provider_name"], "msg": "field required"}]`` → ``provider_name: field required``. */
function formatValidationErrors(items: readonly unknown[]): string | null {
  const parts: string[] = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const { loc, msg } = item as ValidationItem;
    if (typeof msg !== "string" || !msg) continue;
    // loc[0] is the request part ("body"/"query"/"path") — the field path is the rest.
    const field = Array.isArray(loc) ? loc.slice(1).filter((s) => s !== null).join(".") : "";
    parts.push(field ? `${field}: ${msg}` : msg);
  }
  return parts.length ? parts.join("; ") : null;
}

/** First non-empty string among the keys operators actually read. */
function pickDetailString(detail: Record<string, unknown>): string | null {
  for (const key of ["message", "hint", "error", "code"]) {
    const value = detail[key];
    if (typeof value === "string" && value) return value;
  }
  return null;
}

/**
 * Turn an error response body into a human-readable message.
 *
 * FastAPI wraps its HTTPException detail as ``{"detail": "..."}`` (string),
 * ``{"detail": {...}}`` (structured — kontextwerk uses ``message``/``hint``/
 * ``error``/``code``), or ``{"detail": [{loc, msg, type}]}`` (422 validation).
 * Unwrap all three so callers show the actionable text instead of raw JSON or a
 * bare "HTTP 422". Falls back to the body verbatim.
 */
function extractErrorMessage(body: string, status: number): string {
  if (!body) return `HTTP ${status}`;
  try {
    const parsed = JSON.parse(body) as { detail?: unknown };
    const detail = parsed?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (Array.isArray(detail)) {
      const formatted = formatValidationErrors(detail);
      if (formatted) return formatted;
    } else if (detail && typeof detail === "object") {
      const picked = pickDetailString(detail as Record<string, unknown>);
      if (picked) return picked;
    }
  } catch {
    // Not JSON — fall through to the raw body.
  }
  return body;
}

function parseDetail(body: string): unknown {
  if (!body) return undefined;
  try {
    return (JSON.parse(body) as { detail?: unknown })?.detail;
  } catch {
    return undefined;
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

export interface CallOptions {
  realm?: string;
  auth?: boolean;
}

export type ApiCall = <T>(
  path: string,
  init?: RequestInit,
  opts?: CallOptions,
) => Promise<T>;

export function createApiClient(options: ApiClientOptions): ApiCall {
  return async function call<T>(
    path: string,
    init: RequestInit = {},
    opts: CallOptions = {},
  ): Promise<T> {
    const headers = new Headers(init.headers);
    const wantAuth = opts.auth !== false;
    if (wantAuth && options.getToken) {
      const tok = options.getToken();
      if (tok) headers.set("Authorization", `Bearer ${tok}`);
    }
    if (opts.realm) headers.set("X-Kontextwerk-Active-Realm", opts.realm);
    headers.set("Accept", "application/json");

    const resp = await fetch(`${options.baseUrl}${path}`, { ...init, headers });
    if (resp.status === 401) {
      options.onUnauthorized?.();
      throw new ApiError(401, "Bearer-Token ungültig oder abgelaufen");
    }
    if (!resp.ok) {
      const body = await resp.text();
      throw new ApiError(resp.status, extractErrorMessage(body, resp.status), parseDetail(body));
    }
    // 204 and other empty bodies are legitimate success responses; resp.json()
    // would throw a SyntaxError that reads as a client bug.
    const text = await resp.text();
    return (text ? (JSON.parse(text) as T) : (undefined as T));
  };
}
